import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { CANVAS_W, CANVAS_H } from '../components/DeckPage';
import type { PdfFontFiles, PdfRasterFormat, PipelineVectorText } from './pdfExportTypes';

const PDF_DEBUG = process.env.NODE_ENV !== 'production'
  && process.env.REACT_APP_DEBUG_PIPELINE_PDF === 'true';
const configuredScale = Number(process.env.REACT_APP_PIPELINE_PDF_CAPTURE_SCALE);
const FORCED_CAPTURE_SCALE = configuredScale === 2 || configuredScale === 3 ? configuredScale : null;
const configuredConcurrency = Number(process.env.REACT_APP_PIPELINE_PDF_CAPTURE_CONCURRENCY);
const CONCURRENCY_OVERRIDE = configuredConcurrency === 2 ? 2 : 1;
const configuredPngPage = Number(process.env.REACT_APP_PIPELINE_PDF_DEBUG_PNG_PAGE);
const PDF_DEBUG_PNG_PAGE = Number.isInteger(configuredPngPage) && configuredPngPage > 0
  ? configuredPngPage
  : null;
const PDF_JPEG_QUALITY = 0.92;
const A4_WIDTH_MM = 297;
const A4_HEIGHT_MM = 210;
const MM_PER_CANVAS_PX = A4_WIDTH_MM / CANVAS_W;
const PT_PER_CANVAS_PX = MM_PER_CANVAS_PX * 72 / 25.4;
const DECK_HEIGHT_MM = A4_WIDTH_MM * CANVAS_H / CANVAS_W;
const PDF_FONT_FAMILY = 'LiberationSans';

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(index, Math.min(index + chunkSize, bytes.length))));
  }
  return btoa(binary);
};

let pdfFontFilesPromise: Promise<PdfFontFiles> | null = null;
const loadPdfFontFiles = (): Promise<PdfFontFiles> => {
  if (!pdfFontFilesPromise) {
    pdfFontFilesPromise = Promise.all([
      fetch('/fonts/LiberationSans-Regular.ttf'),
      fetch('/fonts/LiberationSans-Bold.ttf'),
      fetch('/fonts/LiberationSans-Italic.ttf'),
      fetch('/fonts/LiberationSans-BoldItalic.ttf'),
    ]).then(async ([regular, bold, italic, boldItalic]) => {
      if (![regular, bold, italic, boldItalic].every((response) => response.ok)) {
        throw new Error('Liberation Sans font files could not be loaded.');
      }
      const [regularBuffer, boldBuffer, italicBuffer, boldItalicBuffer] = await Promise.all([
        regular.arrayBuffer(), bold.arrayBuffer(), italic.arrayBuffer(), boldItalic.arrayBuffer(),
      ]);
      return {
        regular: arrayBufferToBase64(regularBuffer),
        bold: arrayBufferToBase64(boldBuffer),
        italic: arrayBufferToBase64(italicBuffer),
        boldItalic: arrayBufferToBase64(boldItalicBuffer),
      };
    }).catch((error) => {
      pdfFontFilesPromise = null;
      throw error;
    });
  }
  return pdfFontFilesPromise;
};

const registerPdfFonts = (doc: jsPDF, fonts: PdfFontFiles): void => {
  doc.addFileToVFS('LiberationSans-Regular.ttf', fonts.regular);
  doc.addFileToVFS('LiberationSans-Bold.ttf', fonts.bold);
  doc.addFileToVFS('LiberationSans-Italic.ttf', fonts.italic);
  doc.addFileToVFS('LiberationSans-BoldItalic.ttf', fonts.boldItalic);
  doc.addFont('LiberationSans-Regular.ttf', PDF_FONT_FAMILY, 'normal');
  doc.addFont('LiberationSans-Bold.ttf', PDF_FONT_FAMILY, 'bold');
  doc.addFont('LiberationSans-Italic.ttf', PDF_FONT_FAMILY, 'italic');
  doc.addFont('LiberationSans-BoldItalic.ttf', PDF_FONT_FAMILY, 'bolditalic');
  doc.setFont(PDF_FONT_FAMILY, 'normal');
};

const parseRgb = (value: string): [number, number, number] => {
  const match = value.match(/rgba?\(([^)]+)\)/i);
  if (!match) return [0, 0, 0];
  const values = match[1].split(',').map((part) => Number.parseFloat(part.trim()));
  return [values[0] || 0, values[1] || 0, values[2] || 0];
};

const shouldSkipTextElement = (element: HTMLElement): boolean => {
  const style = window.getComputedStyle(element);
  return style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0'
    || element.closest('[aria-hidden="true"]') != null
    || element.tagName === 'SCRIPT' || element.tagName === 'STYLE';
};

const markVectorTextElements = (root: HTMLElement): void => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null = walker.nextNode();
  while (node) {
    const parent = node.parentElement;
    if (parent && node.textContent?.trim() && !shouldSkipTextElement(parent)) {
      parent.dataset.pipelineVectorText = 'true';
    }
    node = walker.nextNode();
  }
};

const cssTransformScale = (element: HTMLElement, axis: 'x' | 'y', root: HTMLElement): number => {
  let scale = 1;
  let current: HTMLElement | null = element;
  while (current && current !== root) {
    const transform = window.getComputedStyle(current).transform;
    if (transform && transform !== 'none') {
      const match = transform.match(/^matrix(?:3d)?\(([^)]+)\)$/);
      if (match) {
        const values = match[1].split(',').map(Number);
        if (values.length === 6) {
          const component = axis === 'x' ? Math.hypot(values[0], values[1]) : Math.hypot(values[2], values[3]);
          if (Number.isFinite(component) && component > 0) scale *= component;
        } else if (values.length === 16) {
          const component = axis === 'x' ? Math.hypot(values[0], values[1], values[2]) : Math.hypot(values[4], values[5], values[6]);
          if (Number.isFinite(component) && component > 0) scale *= component;
        }
      }
    }
    current = current.parentElement;
  }
  return scale;
};

const fontBaselineRatio = (style: CSSStyleDeclaration, fontSize: number): number => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 0.82;
  context.font = `${style.fontStyle} ${style.fontWeight} ${fontSize}px ${style.fontFamily}`;
  const metrics = context.measureText('Ag');
  const ascent = metrics.actualBoundingBoxAscent;
  return Number.isFinite(ascent) && ascent > 0 ? ascent / fontSize : 0.82;
};

const yieldToTask = async (): Promise<void> => {
  await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
};

const collectVectorText = async (root: HTMLElement): Promise<PipelineVectorText[]> => {
  const pageRect = root.getBoundingClientRect();
  const descriptors: PipelineVectorText[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null = walker.nextNode();
  while (node) {
    const text = node.textContent || '';
    const parent = node.parentElement;
    if (parent && text.trim() && !shouldSkipTextElement(parent)) {
      const style = window.getComputedStyle(parent);
      const parentRect = parent.getBoundingClientRect();
      const scaleY = cssTransformScale(parent, 'y', root);
      const fontSize = (Number.parseFloat(style.fontSize) || 8) * scaleY;
      const lineHeight = (style.lineHeight === 'normal' ? (Number.parseFloat(style.fontSize) || 8) * 1.2 : Number.parseFloat(style.lineHeight) || (Number.parseFloat(style.fontSize) || 8) * 1.2) * scaleY;
      const baselineRatio = fontBaselineRatio(style, Number.parseFloat(style.fontSize) || 8);
      const lines = new Map<number, string>();
      for (let index = 0; index < text.length; index += 1) {
        if (text[index] === '\n') continue;
        const range = document.createRange();
        range.setStart(node, index);
        range.setEnd(node, index + 1);
        const rect = range.getBoundingClientRect();
        if (!rect.width && !rect.height) continue;
        const top = Math.round((rect.top - pageRect.top) * 2) / 2;
        lines.set(top, `${lines.get(top) || ''}${text[index]}`);
        if ((index + 1) % 200 === 0) await yieldToTask();
      }
      lines.forEach((line, top) => {
        const clean = line.trim();
        if (!clean) return;
        const svgAnchor = parent instanceof SVGElement ? parent.getAttribute('text-anchor') : null;
        const align = svgAnchor === 'end' || style.textAlign === 'right'
          ? 'right'
          : svgAnchor === 'middle' || style.textAlign === 'center' ? 'center' : 'left';
        const colorValue = parent instanceof SVGElement
          ? window.getComputedStyle(parent).fill || style.color
          : style.color;
        descriptors.push({
          text: clean,
          x: parentRect.left - pageRect.left,
          y: top,
          width: parentRect.width,
          fontSize,
          lineHeight,
          baselineRatio,
          fontStyle: style.fontStyle === 'italic' ? 'italic' : 'normal',
          fontWeight: Number.parseInt(style.fontWeight, 10) >= 600 ? 'bold' : 'normal',
          color: parseRgb(colorValue),
          align,
        });
      });
    }
    node = walker.nextNode();
  }
  return descriptors;
};

const drawVectorText = (doc: jsPDF, descriptors: PipelineVectorText[], pageTopMm: number): void => {
  const pxToMm = MM_PER_CANVAS_PX;
  descriptors.forEach((descriptor) => {
    const style = descriptor.fontStyle === 'italic'
      ? descriptor.fontWeight === 'bold' ? 'bolditalic' : 'italic'
      : descriptor.fontWeight;
    doc.setFont(PDF_FONT_FAMILY, style);
    doc.setFontSize(descriptor.fontSize * PT_PER_CANVAS_PX);
    doc.setTextColor(...descriptor.color);
    const x = (descriptor.x + (descriptor.align === 'center' ? descriptor.width / 2 : descriptor.align === 'right' ? descriptor.width : 0)) * pxToMm;
    const y = pageTopMm + (descriptor.y + descriptor.fontSize * descriptor.baselineRatio) * pxToMm;
    doc.text(descriptor.text, x, y, { align: descriptor.align, baseline: 'alphabetic' });
  });
};

const encodeCanvasJpeg = (canvas: HTMLCanvasElement): Promise<Uint8Array> => new Promise((resolve, reject) => {
  canvas.toBlob(async (blob) => {
    if (!blob) {
      reject(new Error('Could not encode canvas as JPEG.'));
      return;
    }
    try {
      resolve(new Uint8Array(await blob.arrayBuffer()));
    } catch {
      reject(new Error('Could not read encoded JPEG data.'));
    }
  }, 'image/jpeg', PDF_JPEG_QUALITY);
});

const encodeCanvasPng = (canvas: HTMLCanvasElement): Promise<Uint8Array> => new Promise((resolve, reject) => {
  canvas.toBlob(async (blob) => {
    if (!blob) {
      reject(new Error('Could not encode canvas as PNG.'));
      return;
    }
    try {
      resolve(new Uint8Array(await blob.arrayBuffer()));
    } catch {
      reject(new Error('Could not read encoded PNG data.'));
    }
  }, 'image/png');
});

const yieldToBrowser = async (): Promise<void> => {
  await new Promise<void>((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });
};

const waitForProjectDeckFonts = async (): Promise<void> => {
  if (typeof document === 'undefined' || !document.fonts) return;
  await Promise.all([
    document.fonts.load('400 16px "Liberation Sans"'),
    document.fonts.load('700 16px "Liberation Sans"'),
    document.fonts.load('700 52px "Liberation Sans"'),
  ]);
  await document.fonts.ready;
};

const waitForLayoutFrames = async (): Promise<void> => {
  await yieldToBrowser();
  await yieldToBrowser();
};

const logOutOfBoundsElements = (root: HTMLElement, page: number): void => {
  if (!PDF_DEBUG) return;
  const offenders = Array.from(root.querySelectorAll<HTMLElement>('[data-deck-title="true"], [data-overview-header="true"], [data-pipeline-card="true"]'))
    .map((element) => {
      const rect = element.getBoundingClientRect();
      const pageRect = root.getBoundingClientRect();
      return {
        element,
        bounds: {
          left: rect.left - pageRect.left,
          top: rect.top - pageRect.top,
          right: rect.right - pageRect.left,
          bottom: rect.bottom - pageRect.top,
        },
      };
    })
    .filter(({ bounds }) => bounds.left < -2 || bounds.top < -2 || bounds.right > CANVAS_W + 2 || bounds.bottom > CANVAS_H + 2)
    .map(({ element, bounds }) => ({
      kind: element.dataset.deckTitle ? 'title' : element.dataset.overviewHeader ? 'overview-header' : 'card',
      text: (element.textContent || '').trim().slice(0, 80),
      bounds,
    }));
  if (offenders.length) console.warn('[pipeline-pdf] out-of-bounds-elements', { page, offenders });
};

const waitForImages = async (root: HTMLElement): Promise<void> => {
  await Promise.all(Array.from(root.querySelectorAll<HTMLImageElement>('img')).map(async (image) => {
    if (!image.complete) {
      await new Promise<void>((resolve) => {
        image.addEventListener('load', () => resolve(), { once: true });
        image.addEventListener('error', () => resolve(), { once: true });
      });
    }
    if (image.decode) {
      try { await image.decode(); } catch { /* fallback background remains available */ }
    }
  }));
};

const captureScaleForNode = (node: HTMLElement): number => {
  if (FORCED_CAPTURE_SCALE) return FORCED_CAPTURE_SCALE;
  // Dense pages stay at 3x so small card text remains legible on A4. The
  // exporter releases each page buffer before continuing to control memory.
  return 3;
};

const isRecoverableRasterError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return /out of memory|allocation|canvas|image data|unable to create/i.test(message);
};

const waitForBackgroundImages = async (root: HTMLElement): Promise<void> => {
  const urls = new Set<string>();
  [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))].forEach((element) => {
    const background = window.getComputedStyle(element).backgroundImage;
    const match = background.match(/url\(["']?([^"')]+)["']?\)/g) || [];
    match.forEach((value) => {
      const url = value.replace(/^url\(["']?/, '').replace(/["']?\)$/, '');
      if (url) urls.add(url);
    });
  });
  await Promise.all(Array.from(urls).map((url) => new Promise<void>((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve();
    image.onerror = () => resolve();
    image.src = url;
  })));
};

type PdfWorkerPageResult = { addImageMs: number; vectorTextMs: number };
type PdfWorkerFinalizeResult = { buffer: ArrayBuffer; serializationMs: number };

class PdfWorkerAssemblyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PdfWorkerAssemblyError';
  }
}

type PdfWorkerClient = {
  sendPage: (index: number, bytes: Uint8Array, format: PdfRasterFormat, text: PipelineVectorText[]) => Promise<PdfWorkerPageResult>;
  finalize: () => Promise<PdfWorkerFinalizeResult>;
  terminate: () => void;
};

const createPdfWorkerClient = async (fonts: PdfFontFiles): Promise<PdfWorkerClient> => {
  if (typeof Worker === 'undefined') throw new Error('Web Workers are not supported.');
  const worker = new Worker(new URL('./pdfExport.worker.ts', import.meta.url));
  let settled = false;
  let pending: { resolve: (value: PdfWorkerPageResult | PdfWorkerFinalizeResult) => void; reject: (error: Error) => void; kind: 'page' | 'finalize' } | null = null;
  const fail = (error: Error): void => {
    if (settled) return;
    settled = true;
    pending?.reject(error);
    pending = null;
  };
  const ready = new Promise<void>((resolve, reject) => {
    const onMessage = (event: MessageEvent<{
      type: 'ready' | 'page-added' | 'done' | 'error';
      index?: number;
      addImageMs?: number;
      vectorTextMs?: number;
      buffer?: ArrayBuffer;
      serializationMs?: number;
      message?: string;
    }>) => {
      const message = event.data;
      if (message.type === 'ready') {
        resolve();
      } else if (message.type === 'error') {
        const error = new Error(message.message || 'PDF worker failed.');
        fail(error);
        reject(error);
      } else if (message.type === 'page-added' && pending?.kind === 'page') {
        const current = pending;
        pending = null;
        current.resolve({ addImageMs: message.addImageMs || 0, vectorTextMs: message.vectorTextMs || 0 });
      } else if (message.type === 'done' && pending?.kind === 'finalize' && message.buffer) {
        const current = pending;
        pending = null;
        current.resolve({ buffer: message.buffer, serializationMs: message.serializationMs || 0 });
      }
    };
    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', () => {
      const error = new Error('PDF worker stopped unexpectedly.');
      fail(error);
      reject(error);
    }, { once: true });
    worker.postMessage({ type: 'init', fonts } satisfies { type: 'init'; fonts: PdfFontFiles });
  });
  try {
    await ready;
  } catch (error) {
    worker.terminate();
    throw error;
  }
  const request = (message: unknown, transfer: Transferable[], kind: 'page' | 'finalize'): Promise<PdfWorkerPageResult | PdfWorkerFinalizeResult> => {
    if (settled) return Promise.reject(new Error('PDF worker is unavailable.'));
    return new Promise((resolve, reject) => {
      pending = { resolve, reject, kind };
      worker.postMessage(message, transfer);
    });
  };
  return {
    sendPage: async (index, bytes, format, text) => {
      const transferable = bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
        ? bytes.buffer as ArrayBuffer
        : bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
      return await request({ type: 'page', index, bytes: transferable, format, text }, [transferable], 'page') as PdfWorkerPageResult;
    },
    finalize: async () => await request({ type: 'finalize' }, [], 'finalize') as PdfWorkerFinalizeResult,
    terminate: () => {
      if (!settled) worker.postMessage({ type: 'cancel' });
      worker.terminate();
      settled = true;
    },
  };
};

type DownloadTiming = { blobMs: number; initiationMs: number; bytes: number };

const downloadPdfBuffer = (buffer: ArrayBuffer, filename: string): DownloadTiming => {
  const blobStartedAt = performance.now();
  const blob = new Blob([buffer], { type: 'application/pdf' });
  const blobMs = performance.now() - blobStartedAt;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  const initiationStartedAt = performance.now();
  link.click();
  const initiationMs = performance.now() - initiationStartedAt;
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
  return { blobMs, initiationMs, bytes: buffer.byteLength };
};

/**
 * Capture one of the fixed/print deck pages as a local JPEG. This deliberately
 * rasterizes the page as displayed (rather than masking text for the PDF
 * vector pass), so the downloaded image is an exact visual snapshot of the
 * print layout.
 */
export async function capturePipelinePageJpeg(
  container: HTMLElement,
  pageKey: string,
  onProgress?: (stage: 'preparing' | 'capturing' | 'encoding' | 'uploading') => void,
): Promise<{ bytes: Uint8Array; width: number; height: number }> {
  const page = Array.from(container.querySelectorAll<HTMLElement>('[data-pipeline-page]'))
    .find((candidate) => candidate.dataset.pipelinePage === pageKey);
  if (!page) throw new Error(`The ${pageKey} page is not available for image export.`);

  onProgress?.('preparing');
  await waitForProjectDeckFonts();
  await Promise.all([waitForImages(page), waitForBackgroundImages(page)]);
  await waitForLayoutFrames();

  const scale = captureScaleForNode(page);
  onProgress?.('capturing');
  let canvas: HTMLCanvasElement | null = null;
  try {
    const render = (captureScale: number): Promise<HTMLCanvasElement> => html2canvas(page, {
      scale: captureScale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#123b78',
      logging: false,
      foreignObjectRendering: false,
      width: CANVAS_W,
      height: CANVAS_H,
    });
    try {
      canvas = await render(scale);
    } catch (error) {
      if (scale === 3 && !FORCED_CAPTURE_SCALE && isRecoverableRasterError(error)) {
        await yieldToBrowser();
        canvas = await render(2);
      } else {
        throw error;
      }
    }
    onProgress?.('encoding');
    const bytes = await encodeCanvasJpeg(canvas);
    onProgress?.('uploading');
    return { bytes, width: canvas.width, height: canvas.height };
  } finally {
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
      canvas = null;
    }
  }
}

export function jpegBytesToDataUrl(bytes: Uint8Array): string {
  const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  return `data:image/jpeg;base64,${arrayBufferToBase64(buffer)}`;
}

export async function exportPipelineDeckPdf(
  container: HTMLElement,
  onProgress?: (done: number, total: number) => void,
  filename = 'projects-in-the-pipeline.pdf',
): Promise<void> {
  const nodes = Array.from(container.querySelectorAll<HTMLElement>('[data-pipeline-page]'));
  if (nodes.length === 0) throw new Error('Nothing to export');

  const startedAt = performance.now();
  if (PDF_DEBUG) {
    console.debug('[pipeline-pdf] export-start', {
      pages: nodes.length,
      captureScale: FORCED_CAPTURE_SCALE ?? '3x (set REACT_APP_PIPELINE_PDF_CAPTURE_SCALE=2 for low-memory devices)',
      pngPage: PDF_DEBUG_PNG_PAGE,
      logicalSize: `${CANVAS_W}x${CANVAS_H}`,
    });
  }

  const preparationStartedAt = performance.now();
  const timing = {
    preparationMs: 0,
    captureMs: 0,
    encodingMs: 0,
    workerPageInsertionMs: 0,
    workerSerializationMs: 0,
    blobMs: 0,
    downloadInitiationMs: 0,
    pdfBytes: 0,
    pageBytes: [] as number[],
    workerFallback: false,
  };
  const fontStartedAt = performance.now();
  await waitForProjectDeckFonts();
  const pdfFonts = await loadPdfFontFiles();
  if (PDF_DEBUG) console.debug('[pipeline-pdf] fonts-ready', {
    elapsedMs: Math.round(performance.now() - fontStartedAt),
  });
  await Promise.all(nodes.flatMap((node) => [waitForImages(node), waitForBackgroundImages(node)]));
  await waitForLayoutFrames();
  if (PDF_DEBUG) console.debug('[pipeline-pdf] preparation-ready', {
    elapsedMs: Math.round(performance.now() - preparationStartedAt),
  });
  timing.preparationMs = performance.now() - preparationStartedAt;

  const top = (A4_HEIGHT_MM - DECK_HEIGHT_MM) / 2;
  const navigatorInfo = typeof navigator !== 'undefined' ? navigator as Navigator & { deviceMemory?: number; connection?: { saveData?: boolean; effectiveType?: string } } : null;
  if (PDF_DEBUG) console.debug('[pipeline-pdf] device-profile', {
    deviceMemory: navigatorInfo?.deviceMemory ?? null,
    hardwareConcurrency: navigatorInfo?.hardwareConcurrency ?? null,
    captureConcurrency: 1,
    concurrencyOverride: CONCURRENCY_OVERRIDE,
    workerSupported: typeof Worker !== 'undefined',
  });
  let doc: jsPDF | null = null;
  let pdfWorker: PdfWorkerClient | null = null;
  const createMainPdf = (): jsPDF => {
    const fallback = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true, putOnlyUsedFonts: true, floatPrecision: 16 });
    registerPdfFonts(fallback, pdfFonts);
    return fallback;
  };
  try {
    pdfWorker = await createPdfWorkerClient(pdfFonts);
    if (PDF_DEBUG) console.debug('[pipeline-pdf] worker-ready');
  } catch (error) {
    if (PDF_DEBUG) console.warn('[pipeline-pdf] worker-fallback', { reason: error instanceof Error ? error.message : String(error) });
    timing.workerFallback = true;
    doc = createMainPdf();
  }
  let addedPages = 0;
  const addPage = async (index: number, bytes: Uint8Array, format: PdfRasterFormat, text: PipelineVectorText[]): Promise<void> => {
    if (pdfWorker) {
      const workerStartedAt = performance.now();
      const byteSize = bytes.byteLength;
      let result: PdfWorkerPageResult;
      try {
        result = await pdfWorker.sendPage(index, bytes, format, text);
      } catch (error) {
        throw new PdfWorkerAssemblyError(error instanceof Error ? error.message : String(error));
      }
      timing.workerPageInsertionMs += result.addImageMs + result.vectorTextMs;
      if (PDF_DEBUG) console.debug('[pipeline-pdf] worker-page-added', {
        page: index + 1,
        addImageMs: Math.round(result.addImageMs),
        vectorTextCount: text.length,
        vectorTextMs: Math.round(result.vectorTextMs),
        workerRoundTripMs: Math.round(performance.now() - workerStartedAt),
        bytes: byteSize,
      });
    } else if (doc) {
      if (index > 0) doc.addPage('a4', 'landscape');
      const addStartedAt = performance.now();
      doc.addImage(bytes, format, 0, top, A4_WIDTH_MM, DECK_HEIGHT_MM, undefined, 'NONE');
      const vectorStartedAt = performance.now();
      drawVectorText(doc, text, top);
      timing.workerPageInsertionMs += performance.now() - addStartedAt;
      if (PDF_DEBUG) console.debug('[pipeline-pdf] page-added', {
        page: index + 1,
        addImageMs: Math.round(performance.now() - addStartedAt),
        vectorTextCount: text.length,
        vectorTextMs: Math.round(performance.now() - vectorStartedAt),
        bytes: bytes.byteLength,
      });
    } else {
      throw new Error('No PDF assembly target is available.');
    }
    addedPages += 1;
    onProgress?.(addedPages, nodes.length);
  };
  const capturePage = async (index: number): Promise<void> => {
    await yieldToBrowser();
    let canvas: HTMLCanvasElement | null = null;
    let imageBytes: Uint8Array | null = null;
    const pageStartedAt = performance.now();
    let captureScale = captureScaleForNode(nodes[index]);
    const cardCount = nodes[index].querySelectorAll('[data-pipeline-card="true"]').length;
    markVectorTextElements(nodes[index]);
    const textStartedAt = performance.now();
    const vectorText = await collectVectorText(nodes[index]);
    const vectorTextMs = performance.now() - textStartedAt;
    try {
      logOutOfBoundsElements(nodes[index], index + 1);
      if (PDF_DEBUG) console.debug('[pipeline-pdf] capture-start', {
        page: index + 1, width: CANVAS_W, height: CANVAS_H, scale: captureScale, cardCount,
        vectorTextCount: vectorText.length, vectorTextMs: Math.round(vectorTextMs),
        background: nodes[index].querySelector('[data-deck-background="true"]')?.getAttribute('src')?.slice(0, 80) || 'fallback-css',
        concurrency: 1, timestamp: new Date().toISOString(),
      });
      const renderRaster = (scale: number): Promise<HTMLCanvasElement> => html2canvas(nodes[index], {
          scale,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#123b78',
          logging: false,
          foreignObjectRendering: false,
          width: CANVAS_W,
          height: CANVAS_H,
          onclone: (clonedDocument) => {
            clonedDocument.querySelectorAll<HTMLElement>('[data-pipeline-vector-text="true"]').forEach((element) => {
              element.style.setProperty('color', 'transparent', 'important');
              element.style.setProperty('text-shadow', 'none', 'important');
            });
            clonedDocument.querySelectorAll<SVGElement>('[data-pipeline-vector-text="true"]').forEach((element) => {
              element.style.setProperty('fill', 'transparent', 'important');
              element.style.setProperty('stroke', 'transparent', 'important');
            });
          },
        });
      try {
        canvas = await renderRaster(captureScale);
      } catch (error) {
        if (captureScale === 3 && !FORCED_CAPTURE_SCALE && isRecoverableRasterError(error)) {
          captureScale = 2;
          if (PDF_DEBUG) console.warn('[pipeline-pdf] raster-fallback', { page: index + 1, from: 3, to: 2, reason: error instanceof Error ? error.message : String(error) });
          await yieldToBrowser();
          canvas = await renderRaster(captureScale);
        } else {
          throw error;
        }
      }
      const captureMs = performance.now() - pageStartedAt;
      timing.captureMs += captureMs;
      await yieldToBrowser();
      const usePng = PDF_DEBUG_PNG_PAGE === index + 1;
      const encodingStartedAt = performance.now();
      imageBytes = usePng ? await encodeCanvasPng(canvas) : await encodeCanvasJpeg(canvas);
      const encodingMs = performance.now() - encodingStartedAt;
      timing.encodingMs += encodingMs;
      timing.pageBytes.push(imageBytes.byteLength);
      if (PDF_DEBUG) console.debug('[pipeline-pdf] capture-ready', {
        page: index + 1, canvasWidth: canvas.width, canvasHeight: canvas.height,
        format: usePng ? 'PNG' : 'JPEG', bytes: imageBytes.byteLength,
        captureMs: Math.round(captureMs),
        encodingMs: Math.round(encodingMs),
        vectorTextCount: vectorText.length,
        elapsedMs: Math.round(performance.now() - pageStartedAt),
      });
      await yieldToBrowser();
      await addPage(index, imageBytes, usePng ? 'PNG' : 'JPEG', vectorText);
      await yieldToBrowser();
    } catch (error) {
      if (error instanceof PdfWorkerAssemblyError) throw error;
      throw new Error(`Could not export PDF page ${index + 1}: ${error instanceof Error ? error.message : 'unknown error'}`);
    } finally {
      imageBytes = null;
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
        canvas = null;
      }
    }
  };
  const captureAllPages = async (): Promise<void> => {
    for (let index = 0; index < nodes.length; index += 1) await capturePage(index);
  };
  try {
    await captureAllPages();
    if (addedPages !== nodes.length) throw new Error(`Could not export PDF: ${nodes.length - addedPages} page(s) were not inserted`);
    await yieldToBrowser();
    if (pdfWorker) {
      let buffer: ArrayBuffer;
      try {
        const result = await pdfWorker.finalize();
        buffer = result.buffer;
        timing.workerSerializationMs = result.serializationMs;
      } catch (error) {
        throw new PdfWorkerAssemblyError(error instanceof Error ? error.message : String(error));
      }
      const downloadTiming = downloadPdfBuffer(buffer, filename);
      timing.blobMs = downloadTiming.blobMs;
      timing.downloadInitiationMs = downloadTiming.initiationMs;
      timing.pdfBytes = buffer.byteLength;
      if (PDF_DEBUG) console.debug('[pipeline-pdf] worker-serialized', { serializationMs: Math.round(timing.workerSerializationMs), bytes: buffer.byteLength });
    } else if (doc) {
      const saveStartedAt = performance.now();
      const buffer = doc.output('arraybuffer');
      timing.workerSerializationMs = performance.now() - saveStartedAt;
      const downloadTiming = downloadPdfBuffer(buffer, filename);
      timing.blobMs = downloadTiming.blobMs;
      timing.downloadInitiationMs = downloadTiming.initiationMs;
      timing.pdfBytes = buffer.byteLength;
      if (PDF_DEBUG) console.debug('[pipeline-pdf] export-save', { saveMs: Math.round(timing.workerSerializationMs), bytes: buffer.byteLength });
    }
    await yieldToBrowser();
  } catch (error) {
    if (!(error instanceof PdfWorkerAssemblyError) || !pdfWorker) throw error;
    if (PDF_DEBUG) console.warn('[pipeline-pdf] worker-runtime-fallback', { reason: error.message });
    pdfWorker.terminate();
    pdfWorker = null;
    doc = createMainPdf();
    timing.workerFallback = true;
    addedPages = 0;
    await captureAllPages();
    if (!doc || addedPages !== nodes.length) throw new Error('Could not recover PDF export after worker failure.');
    const saveStartedAt = performance.now();
    const buffer = doc.output('arraybuffer');
    timing.workerSerializationMs = performance.now() - saveStartedAt;
    const downloadTiming = downloadPdfBuffer(buffer, filename);
    timing.blobMs = downloadTiming.blobMs;
    timing.downloadInitiationMs = downloadTiming.initiationMs;
    timing.pdfBytes = buffer.byteLength;
  } finally {
    pdfWorker?.terminate();
    if (PDF_DEBUG) {
      console.debug('[pipeline-pdf] export-complete', {
        status: addedPages === nodes.length ? 'complete' : 'failed',
        pages: nodes.length,
        pagesInserted: addedPages,
        elapsedMs: Math.round(performance.now() - startedAt),
        preparationMs: Math.round(timing.preparationMs),
        captureMs: Math.round(timing.captureMs),
        encodingMs: Math.round(timing.encodingMs),
        workerPageInsertionMs: Math.round(timing.workerPageInsertionMs),
        workerSerializationMs: Math.round(timing.workerSerializationMs),
        blobMs: Math.round(timing.blobMs),
        downloadInitiationMs: Math.round(timing.downloadInitiationMs),
        pdfBytes: timing.pdfBytes,
        pageBytes: timing.pageBytes,
        workerFallback: timing.workerFallback,
        captureScale: FORCED_CAPTURE_SCALE ?? 3,
        jpegQuality: PDF_JPEG_QUALITY,
        browser: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        platform: typeof navigator !== 'undefined' ? navigator.platform : null,
        userAgentPlatform: typeof navigator !== 'undefined' ? (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? null : null,
        deviceMemory: typeof navigator !== 'undefined' ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null : null,
        hardwareConcurrency: typeof navigator !== 'undefined' ? navigator.hardwareConcurrency ?? null : null,
      });
    }
  }
}
